import { Box, Collapse, IconButton, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { useState } from 'react';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import { StyledHeading } from '../styles/styles';
import type FundOpportunity from '../../models/FundOpportunity';
import type { AdminFundUpdateEntry, CmsBlock } from '../../models/FundUpdateEntry';
import type {
  PageMode,
  SaveStatus,
} from '../admin/newContentManagement/newContentManagementEditorTypes';
import FundHighlightSection from './FundHighlightSection';
import type { SimplifiedFundUpdateEditorHandle } from './SimplifiedFundUpdateEditor';
import SimplifiedFundUpdateEditor from './SimplifiedFundUpdateEditor';
import { mostRecentUpdateId, resolveHighlightEditState } from './fundUpdatesAdminUtils';
import CreateDraftRow from './CreateDraftRow';
import FundUpdateHeaderBar from './FundUpdateHeaderBar';
import UpdateListRow from './UpdateListRow';
import type { useFundUpdatesAdmin } from './useFundUpdatesAdmin';

const CENTERED_ROW_DISPLAY = 'flex';

type FundUpdatesController = ReturnType<typeof useFundUpdatesAdmin>;

interface SharedPanelProps {
  controller: FundUpdatesController;
  fundOpportunity: FundOpportunity;
  fundName: string;
}

function editorRef(controller: FundUpdatesController, id: string) {
  return controller.getCmsRef(id) as unknown as React.RefObject<SimplifiedFundUpdateEditorHandle>;
}

export function NoUpdatesMessage() {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      No unpublished updates yet — click Add Update to create one.
    </Typography>
  );
}

function HighlightForUpdate({
  controller,
  fundName,
  update,
  canEditCard,
}: SharedPanelProps & {
  update: AdminFundUpdateEntry;
  canEditCard: boolean;
}) {
  const { state } = controller;
  const id = update.externalId;
  const suggestion = state.highlightSuggestion?.id === id ? state.highlightSuggestion : null;
  return (
    <FundHighlightSection
      update={update}
      fundName={fundName}
      canEditCard={canEditCard}
      isOpen={state.highlightOpenIds.has(id)}
      isEditing={state.highlightEditingId === id}
      isGenerating={state.highlightGeneratingIds.has(id)}
      isGeneratingPro={state.highlightGeneratingProIds.has(id)}
      isCmsBusy={state.cmsBusyIds.has(id)}
      isSavingForGenerate={state.saveForGenerateIds.has(id)}
      isSavingForGeneratePro={state.saveForGenerateProIds.has(id)}
      suggestion={suggestion}
      editState={resolveHighlightEditState(update, state.highlightEdits)}
      refinementPrompt={state.highlightRefinementPrompts[id] ?? ''}
      onToggleOpen={() => controller.toggleHighlightSection(id)}
      onStartEdit={() => controller.startHighlightEdit(update)}
      onCancelEdit={controller.cancelHighlightEdit}
      onEditChange={(field, value) => controller.handleHighlightEditChange(id, field, value)}
      onSave={() => controller.handleSaveHighlight(update)}
      onClear={() => controller.handleClearHighlight(update)}
      onGenerate={(usePro, prompt) => controller.handleGenerateHighlight(update, usePro, prompt)}
      onAcceptSuggestion={() => controller.handleAcceptSuggestion(update)}
      onRejectSuggestion={controller.handleRejectSuggestion}
      onRefinementPromptChange={(value) => controller.handleRefinementPromptChange(id, value)}
      onSuggestionImageSelect={(imageUrl) =>
        controller.handleSuggestionImageSelect(update, imageUrl)
      }
    />
  );
}

export type ToolbarStateChangeHandler = (state: {
  saveStatus: SaveStatus;
  pageMode: PageMode;
  setPageMode: (mode: PageMode) => void;
  saveNow: () => void;
  toolbarRef: React.RefObject<HTMLDivElement | null>;
}) => void;

/**
 * The content area for whichever update is currently "open" — the same
 * drag-and-drop block editor as the Fund Information page, with the Fund
 * Highlight Card (for the native app home screen) kept at the end of it, all
 * inside one bordered panel with its own header bar (date, Save, Publish,
 * Compress, Delete). Save/Publish/View also still live in the page-level
 * toolbar, which this reports its state up to via `onToolbarStateChange`.
 */
export function OpenUpdateCanvas(
  props: SharedPanelProps & {
    update: AdminFundUpdateEntry;
    onOpen: (id: string | null) => void;
    onToolbarStateChange?: ToolbarStateChangeHandler;
  },
) {
  const { controller, fundOpportunity, update, onOpen, onToolbarStateChange } = props;
  const id = update.externalId;
  const [localToolbarState, setLocalToolbarState] = useState<{
    saveNow: () => void;
    saveStatus: SaveStatus;
  } | null>(null);

  const handleToolbarStateChange: ToolbarStateChangeHandler = (toolbarState) => {
    setLocalToolbarState({ saveNow: toolbarState.saveNow, saveStatus: toolbarState.saveStatus });
    onToolbarStateChange?.(toolbarState);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: Palette.default.border,
        borderRadius: RADIUS.section,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      <FundUpdateHeaderBar
        controller={controller}
        update={update}
        open
        onOpen={onOpen}
        saveNow={localToolbarState?.saveNow}
        saveStatus={localToolbarState?.saveStatus}
      />
      <Box sx={{ p: 2 }}>
        <SimplifiedFundUpdateEditor
          key={`${id}-${controller.state.revertNonce[id] ?? 0}`}
          ref={editorRef(controller, id)}
          update={update}
          fundOpportunity={fundOpportunity}
          onBusyChange={(busy) => controller.handleCmsBusyChange(id, busy)}
          onToolbarStateChange={handleToolbarStateChange}
          onContentChange={(content) => {
            // Fund Update content never legitimately contains a ChartData block
            // (useBlockActions' addBlock refuses to add one) — ContentItem is
            // just the wider type the shared block editor works in terms of.
            const cmsContent = content as unknown as CmsBlock[];
            controller.setState((prev) => ({
              ...prev,
              drafts: prev.drafts.map((d) =>
                d.externalId === id ? { ...d, content: cmsContent } : d,
              ),
              published: prev.published.map((p) =>
                p.externalId === id ? { ...p, content: cmsContent } : p,
              ),
            }));
          }}
          renderAfterContent={({ hasContent, pageMode }) =>
            hasContent &&
            pageMode === 'edit' && <HighlightForUpdate {...props} update={update} canEditCard />
          }
        />
      </Box>
    </Box>
  );
}

/**
 * Renders one update in its stable list position — the full editable canvas
 * when it's the currently "open" one, otherwise the read-only row. Keeping
 * this choice purely about RENDER MODE (not list position) means opening a
 * different update for editing never reorders the list around it.
 */
export function UpdateRowOrCanvas(
  props: SharedPanelProps & {
    update: AdminFundUpdateEntry;
    openUpdateId: string | null;
    onOpen: (id: string | null) => void;
    onToolbarStateChange?: ToolbarStateChangeHandler;
    /** When given, the read-only row can fold its content away. */
    listExpanded?: boolean;
    onToggleListExpanded?: () => void;
  },
) {
  const {
    update,
    openUpdateId,
    onOpen,
    onToolbarStateChange,
    listExpanded,
    onToggleListExpanded,
    ...rest
  } = props;
  if (update.externalId === openUpdateId) {
    return (
      <OpenUpdateCanvas
        {...rest}
        update={update}
        onOpen={onOpen}
        onToolbarStateChange={onToolbarStateChange}
      />
    );
  }
  return (
    <UpdateListRow
      controller={rest.controller}
      update={update}
      onOpen={onOpen}
      expanded={listExpanded}
      onToggleExpanded={onToggleListExpanded}
    />
  );
}

/**
 * New Updates and Existing Updates share this collapsible header: a clickable
 * title + expand/collapse icon. Each header owns the space below itself only
 * (mb: 1 open, +20px to 28px when collapsed, since there's no body content
 * left to supply that visual weight) — `topSpacing` adds separation above the
 * header too, but only where nothing before it already provides that gap.
 * Existing Updates sits directly below New Updates' own bottom margin, so it
 * passes topSpacing={false} to avoid stacking a second gap on top of that one.
 */
function CollapsibleSectionHeader({
  title,
  expanded,
  onToggle,
  topSpacing = true,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  topSpacing?: boolean;
}) {
  return (
    <Box
      sx={{
        display: CENTERED_ROW_DISPLAY,
        alignItems: 'center',
        gap: 0.5,
        mt: topSpacing ? 3 : 0,
        mb: expanded ? 1 : '28px',
        cursor: 'pointer',
      }}
      onClick={onToggle}
    >
      <StyledHeading headerVariant="heading2" reduceBottomPadding>
        {title}
      </StyledHeading>
      <IconButton size="small">
        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </IconButton>
    </Box>
  );
}

export function NewUpdatesSection(
  props: SharedPanelProps & {
    openUpdateId: string | null;
    onOpen: (id: string | null) => void;
    onToolbarStateChange?: ToolbarStateChangeHandler;
  },
) {
  const { controller, fundOpportunity, openUpdateId, onOpen } = props;
  const [expanded, setExpanded] = useState(true);
  const drafts = controller.state.drafts;
  return (
    <Box>
      <CollapsibleSectionHeader
        title="New Updates (Unpublished)"
        expanded={expanded}
        onToggle={() => setExpanded((prev) => !prev)}
      />
      <Collapse in={expanded}>
        <CreateDraftRow
          controller={controller}
          fundOpportunity={fundOpportunity}
          onCreated={onOpen}
        />
        {drafts.length === 0 && <NoUpdatesMessage />}
        {drafts.map((draft) => (
          <UpdateRowOrCanvas
            key={draft.externalId}
            {...props}
            update={draft}
            openUpdateId={openUpdateId}
            onOpen={onOpen}
          />
        ))}
      </Collapse>
    </Box>
  );
}

export function ExistingUpdatesSection(
  props: SharedPanelProps & {
    openUpdateId: string | null;
    onOpen: (id: string | null) => void;
    onToolbarStateChange?: ToolbarStateChangeHandler;
  },
) {
  const { controller, openUpdateId, onOpen } = props;
  const [expanded, setExpanded] = useState(true);
  const items = controller.state.published;
  // Only the most recent update starts expanded — a fund's history gets long. Anything the admin
  // has toggled by hand keeps its state.
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({});
  const newestId = mostRecentUpdateId(items);
  return (
    <Box>
      <CollapsibleSectionHeader
        title="Existing Updates"
        expanded={expanded}
        onToggle={() => setExpanded((prev) => !prev)}
        topSpacing={false}
      />
      <Collapse in={expanded}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            There are currently no published updates for this fund.
          </Typography>
        ) : (
          items.map((update) => (
            <UpdateRowOrCanvas
              key={update.externalId}
              {...props}
              update={update}
              openUpdateId={openUpdateId}
              onOpen={onOpen}
              listExpanded={expandedOverrides[update.externalId] ?? update.externalId === newestId}
              onToggleListExpanded={() =>
                setExpandedOverrides((prev) => ({
                  ...prev,
                  [update.externalId]: !(prev[update.externalId] ?? update.externalId === newestId),
                }))
              }
            />
          ))
        )}
      </Collapse>
    </Box>
  );
}
